import { useReducer, useCallback } from "react"
import dayjs from "dayjs"

import { formatStringToFloatDecimalCase } from "src/utils/format-decimal"

const DATE_FORMAT = "YYYY-MM-DD"

const initialState = {
    loading: false,
    ultimo: { data_referencia: "", spread_credito: "", percentual_pupar: "" },
    rows: [],
    table: [],
    alert: { open: false, message: "", type: "" },
    openModal: false,
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_LOADING":
            return { ...state, loading: action.payload }
        case "SET_ULTIMO":
            return { ...state, ultimo: action.payload }
        case "SET_ROWS":
            return { ...state, rows: action.payload }
        case "SET_TABLE":
            return { ...state, table: action.payload }
        case "SET_ALERT":
            return { ...state, alert: { ...state.alert, ...action.payload } }
        case "CLOSE_ALERT":
            return { ...state, alert: { ...state.alert, open: false } }
        case "SET_MODAL":
            return { ...state, openModal: action.payload }
        default:
            return state
    }
}

const ordenaPorDataReferencia = (arr, order = "asc") =>
    [...arr].sort((a, b) => {
        const dateA = a?.DataReferencia
            ? new Date(a.DataReferencia)
            : new Date(0)
        const dateB = b?.DataReferencia
            ? new Date(b.DataReferencia)
            : new Date(0)
        return order === "desc" ? dateB - dateA : dateA - dateB
    })

export const usePrecosProcessados = (
    emissaoService,
    cetipIsin,
    dadosEmpresa
) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    const validateValue = useCallback(() => cetipIsin?.length > 4, [cetipIsin])

    const buildTable = useCallback(() => {
        dispatch({
            type: "SET_TABLE",
            payload: [
                {
                    cells: [
                        `CETIP: ${dadosEmpresa?.CETIP}`,
                        `ISIN: ${dadosEmpresa?.ISIN}`,
                    ],
                },
            ],
        })
    }, [dadosEmpresa])

    const loadTableRows = useCallback(async () => {
        if (!validateValue()) {
            dispatch({ type: "SET_MODAL", payload: true })
            return
        }
        buildTable()
        dispatch({ type: "SET_LOADING", payload: true })
        try {
            const { body } = await emissaoService.precosProcessados(
                "get",
                `?CETIP=${cetipIsin}&DataReferencia__desc=true`
            )
            const { results } = body
            if (!results || results.length === 0) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Não há dados para o CETIP consultado.",
                        type: "warning",
                    },
                })
            } else {
                dispatch({
                    type: "SET_ALERT",
                    payload: { open: false, message: "", type: "" },
                })
            }
            const rows = ordenaPorDataReferencia(results, "desc").map(
                (obj, i) => ({ ...obj, id: i })
            )
            dispatch({ type: "SET_ROWS", payload: rows })
            dispatch({ type: "SET_MODAL", payload: true })
        } catch {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Não há dados para o CETIP consultado.",
                    type: "warning",
                },
            })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [cetipIsin, emissaoService, validateValue, buildTable])

    const addRow = useCallback(async () => {
        let lastItem = state.rows[0]
        if (!state.rows.length) {
            lastItem = {
                CETIP: cetipIsin,
                DataReferencia: dayjs().format(DATE_FORMAT),
                TaxaMTM: 0,
                PercentualPUPAR: 0,
                SpreadCredito: 0,
            }
        }
        dispatch({ type: "SET_LOADING", payload: true })
        try {
            await emissaoService.precosProcessados("post", lastItem)
            await loadTableRows()
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [state.rows, cetipIsin, emissaoService, loadTableRows])

    const deleteRow = useCallback(
        async (row) => {
            const { ID } = row
            dispatch({ type: "SET_LOADING", payload: true })
            try {
                await emissaoService.precosProcessados("delete", ID)
                await loadTableRows()
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, loadTableRows]
    )

    const updateRow = useCallback(
        async (row) => {
            const { ID } = row
            dispatch({ type: "SET_LOADING", payload: true })
            try {
                await emissaoService.precosProcessados("patch", ID, row)
                const updated = state.rows.map((r) =>
                    r.ID === ID ? { ...r, ...row } : r
                )
                dispatch({ type: "SET_ROWS", payload: updated })
                return row
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, state.rows]
    )

    const formatValue = useCallback((params, min, max) => {
        const { value } = params
        return formatStringToFloatDecimalCase(value, min, max)
    }, [])

    return {
        state,
        actions: {
            loadTableRows,
            addRow,
            deleteRow,
            updateRow,
            setAlert: (payload) => dispatch({ type: "SET_ALERT", payload }),
            closeAlert: () => dispatch({ type: "CLOSE_ALERT" }),
            setModal: (open) => dispatch({ type: "SET_MODAL", payload: open }),
            formatValue,
        },
    }
}
